import React from 'react';
import SEO from '../components/SEO';
import ScrollIndicator from '../components/ScrollIndicator';
import './LegalPages.css';

const sections = [
  { id: 'scope', label: 'Scope of Services' },
  { id: 'registration', label: 'Professional Registration' },
  { id: 'consent', label: 'Informed Consent' },
  { id: 'booking', label: 'Appointment Booking and Attendance' },
  { id: 'cancellations', label: 'Cancellations and No-Shows' },
  { id: 'fees', label: 'Fees and Payment' },
  { id: 'responsibilities', label: 'Client Responsibilities' },
  { id: 'confidentiality', label: 'Confidentiality and Privacy' },
  { id: 'telehealth', label: 'Telehealth Services' },
  { id: 'limitations', label: 'Limitations and Scope of Practice' },
  { id: 'emergency', label: 'Emergency Procedures' },
  { id: 'complaints', label: 'Complaints and Feedback' },
  { id: 'termination', label: 'Termination of Services' },
  { id: 'consumer-law', label: 'Australian Consumer Law' },
  { id: 'disclaimer', label: 'Disclaimer' },
  { id: 'changes', label: 'Changes to Terms' },
  { id: 'contact', label: 'Contact Information' },
];

const TermsAndConditions = () => {
  return (
    <div className="page-legal" data-testid="terms-page">
      <SEO
        title="Terms and Conditions | Clinical Minds Psychology"
        description="Terms and conditions for Clinical Minds Psychology services, including fees, payment, cancellations, confidentiality and telehealth."
        canonicalUrl="https://www.clinicalminds.net/terms-and-conditions"
        noindex={false}
      />
      <ScrollIndicator />

      <section id="main-content" className="legal-hero-compact">
        <div className="container">
          <span className="section-label">Terms</span>
          <h1 className="heading-hero">Terms and Conditions</h1>
          <p className="legal-subline">Clinical Minds Psychology | ABN 96 150 581 605 | Effective July 2026</p>
        </div>
      </section>

      <section className="legal-content">
        <div className="container">
          <div className="legal-text">

            {/* Table of Contents */}
            <nav className="legal-toc" data-testid="terms-toc">
              <h3 className="heading-3">Contents</h3>
              <ol className="legal-toc-list">
                {sections.map((s) => (
                  <li key={s.id}><a href={`#${s.id}`}>{s.label}</a></li>
                ))}
              </ol>
            </nav>

            {/* Sections */}
            <div id="scope" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">1. Scope of Services</h2>
              <p className="body-medium">
                Clinical Minds Psychology provides professional psychology services including:
              </p>
              <ul className="legal-list">
                <li>Individual psychological therapy and counselling</li>
                <li>Psychological assessments and evaluations</li>
                <li>Mental health diagnosis and treatment planning</li>
                <li>Virtual reality and digital therapeutic interventions</li>
                <li>Telehealth and face-to-face consultations</li>
              </ul>
              <p className="body-medium">
                All services are delivered within the scope of professional competence and in accordance with the Psychology Board of Australia Code of Conduct (2025), the APS Code of Ethics, and Australian consumer law.
              </p>
            </div>

            <div id="registration" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">2. Professional Registration</h2>
              <p className="body-medium">
                Lachlan Beaton is a Clinical Psychologist (Registrar) registered with the Australian Health Practitioner Regulation Agency (AHPRA), Registration Number PSY0002785698. Services are provided under the supervision requirements of the Psychology Board of Australia.
              </p>
            </div>

            <div id="consent" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">3. Informed Consent</h2>
              <p className="body-medium">
                By booking and attending appointments, and signing our informed consent, you provide informed consent to receive psychological services, understanding the nature, risks, benefits, and alternatives. You may withdraw your consent and discontinue services at any time.
              </p>
              <p className="body-medium">
                You will be provided with an informed consent form detailing what this means, how your confidentiality is protected, any exceptions to this, and how we treat your personal information. You may sign this form or discuss any concerns with us.
              </p>
            </div>

            <div id="booking" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">4. Appointment Booking and Attendance</h2>
              <p className="body-medium">
                Appointments can be requested online, by SMS, email or phone. A confirmation email is sent when a booking is made, with an SMS and email reminder three days prior to your appointment. Prior to your first appointment, you will be sent intake and consent forms by email, which can be completed online. Please read them carefully and bring any questions to your first appointment. Sessions are typically 50 minutes in duration.
              </p>
            </div>

            <div id="cancellations" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">5. Cancellations and No-Shows</h2>
              <p className="body-medium">
                24 hours' notice is required for all cancellations and reschedules. Cancellations can be made by calling our office on 02 6600 2710, or by SMS or email.
              </p>
              <ul className="legal-list">
                <li><strong>Late cancellations</strong> (less than 24 hours' notice) will incur the full session fee.</li>
                <li><strong>No-shows</strong> will be charged the full session fee.</li>
                <li>Where you have authorised a stored card in your intake form, you authorise Clinical Minds to charge that card for any cancellation or non-attendance fee, in line with these Terms.</li>
                <li>Repeated late cancellations or no-shows may result in termination of services.</li>
              </ul>
              <p className="body-medium">
                If you believe extenuating circumstances apply, please contact us as soon as possible. We will consider requests for fee waivers in line with Australian Consumer Law and compassionate grounds.
              </p>
            </div>

            <div id="fees" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">6. Fees and Payment</h2>
              <h3 className="heading-3">Session Fees</h3>
              <ul className="legal-list">
                <li><strong>Individual therapy sessions:</strong> $220 per 50-minute session</li>
                <li><strong>Psychological assessment fees</strong> (e.g. autism, ADHD, FASD, learning, cognitive and intellectual disability assessment): discussed prior to booking, as fees depend on the scope of the assessment. There is generally no Medicare rebate for psychological assessments, although in some cases a paediatrician can refer for a complex neurodevelopmental assessment where some rebates apply.</li>
                <li><strong>Medicare rebate for eligible therapy sessions:</strong> $101.55 (requires a Mental Health Treatment Plan from your GP)</li>
              </ul>

              <h3 className="heading-3">Payment and stored cards</h3>
              <p className="body-medium">
                To book appointments and process payments efficiently, card details may be securely stored through our payment provider. Your full card number is held by the provider, not by Clinical Minds. By providing a stored card you authorise Clinical Minds to charge it for the fees that apply to the services you receive, including session fees and any cancellation or non-attendance fees, in line with these Terms.
              </p>
              <p className="body-medium">
                Payment methods: credit card payments can be made online; EFTPOS is available for in-person sessions; private health insurance claims can be processed online after sessions; NDIS funding is accepted and invoicing is available.
              </p>

              <h3 className="heading-3">Medicare, bulk billing and insurance</h3>
              <ul className="legal-list">
                <li>Medicare rebates require a valid Mental Health Treatment Plan from your GP.</li>
                <li>Under the usual arrangement you pay the session fee and the Medicare rebate is returned to you, typically deposited within 24 hours.</li>
                <li>Where we bulk bill a service, you assign your Medicare benefit to Clinical Minds, we claim the benefit directly from Medicare, and you are not charged for that service. You will be asked to confirm this assignment for each bulk billed service as required by Medicare.</li>
                <li>Private health insurance claims are your responsibility to submit; receipts and documentation will be provided.</li>
                <li>Fees are reviewed periodically and may change; notice will be provided to active clients prior to any changes.</li>
                <li>Refunds for prepaid sessions will be considered in line with Australian Consumer Law.</li>
              </ul>
            </div>

            <div id="responsibilities" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">7. Client Responsibilities</h2>
              <ul className="legal-list">
                <li>Arrive on time for scheduled appointments</li>
                <li>Complete required intake forms and assessments honestly and thoroughly</li>
                <li>Inform us of any changes to your health, medications, or circumstances</li>
                <li>Maintain confidentiality of other clients you may encounter</li>
                <li>Treat staff and facilities with respect</li>
                <li>Pay fees as agreed and on time</li>
                <li>For telehealth, ensure a private, quiet space with reliable internet</li>
              </ul>
            </div>

            <div id="confidentiality" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">8. Confidentiality and Privacy</h2>
              <p className="body-medium">
                All personal information is managed in accordance with the Australian Privacy Principles (APPs) and the Privacy Act 1988 (Cth). Your records are stored securely using password-protected and encrypted systems. Records are retained for at least seven years after your last contact, or until age 25 for clients under 18, as required by law. More information can be found in our <a href="/privacy-policy">Privacy Policy</a>.
              </p>
              <p className="body-medium">
                Information shared during sessions is strictly confidential, except in the following circumstances:
              </p>
              <ul className="legal-list">
                <li>You provide written consent to share information</li>
                <li>There is a serious risk of harm to yourself or others</li>
                <li>Child abuse or neglect is suspected (mandatory reporting)</li>
                <li>Court orders or legal requirements mandate disclosure</li>
                <li>Professional supervision and consultation, using de-identified information wherever possible</li>
              </ul>
              <p className="body-medium">
                You have the right to access and request correction of your personal information. Requests will be processed in accordance with privacy laws and professional guidelines. For further details, please refer to our <a href="/privacy-policy">Privacy Policy</a>.
              </p>
            </div>

            <div id="telehealth" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">9. Telehealth Services</h2>
              <p className="body-medium">Requirements:</p>
              <ul className="legal-list">
                <li>Stable internet connection and compatible device</li>
                <li>A private, quiet space free from interruptions</li>
                <li>A valid Australian address (services are limited to Australian residents)</li>
                <li>Current emergency contact information</li>
              </ul>
              <p className="body-medium">
                Compliance: telehealth services are provided in compliance with privacy and security standards for digital health, as outlined by the Psychology Board of Australia and the Privacy Act 1988. Services are available only to clients residing in Australia.
              </p>
            </div>

            <div id="limitations" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">10. Limitations and Scope of Practice</h2>
              <ul className="legal-list">
                <li>Services are not suitable for emergencies or crisis situations. If you are in crisis or immediate danger, please contact emergency services (000) or present to your nearest hospital.</li>
                <li>Medication prescriptions are not provided; please see your GP or psychiatrist.</li>
                <li>Court reports and legal assessments require separate arrangements.</li>
                <li>Services may not be suitable for all conditions, and referrals will be made when appropriate.</li>
              </ul>
            </div>

            <div id="emergency" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">11. Emergency Procedures</h2>
              <p className="body-medium">In case of mental health emergency or crisis:</p>
              <ul className="legal-list">
                <li><strong>Immediate danger:</strong> call 000 (Police, Ambulance)</li>
                <li><strong>Crisis support:</strong> Lifeline 13 11 14</li>
                <li><strong>Mental Health Line:</strong> 1800 011 511 (24/7)</li>
                <li><strong>Beyond Blue:</strong> 1300 22 4636</li>
                <li>Present to your nearest hospital emergency department</li>
              </ul>
            </div>

            <div id="complaints" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">12. Complaints and Feedback</h2>
              <p className="body-medium">We welcome feedback about our services. If you have concerns:</p>
              <ul className="legal-list">
                <li>Speak directly with Lachlan Beaton about any issues</li>
                <li>Contact us in writing at <a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a></li>
                <li>Lodge a complaint with the Psychology Board of Australia</li>
                <li>Contact the Australian Health Practitioner Regulation Agency (AHPRA)</li>
                <li>Contact the NSW Health Care Complaints Commission (HCCC) for concerns about health service delivery</li>
              </ul>
              <p className="body-medium">
                You will not be penalised or disadvantaged for raising concerns or making a complaint.
              </p>
            </div>

            <div id="termination" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">13. Termination of Services</h2>
              <ul className="legal-list">
                <li>Either party may terminate the therapeutic relationship with appropriate notice.</li>
                <li>Clients may discontinue services at any time.</li>
                <li>We may terminate services for non-payment, repeated missed appointments, or inappropriate behaviour.</li>
                <li>Referrals to other practitioners will be provided when appropriate.</li>
                <li>Clinical records will be maintained as per professional requirements.</li>
              </ul>
            </div>

            <div id="consumer-law" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">14. Australian Consumer Law</h2>
              <p className="body-medium">
                Our services are provided in accordance with Australian Consumer Law. You have rights under this law that cannot be excluded, including the right to services that are provided with due care and skill, and are fit for purpose. Nothing in these terms excludes, restricts, or modifies your rights under Australian Consumer Law.
              </p>
            </div>

            <div id="disclaimer" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">15. Disclaimer</h2>
              <p className="body-medium">
                Psychological services are intended to support mental health and wellbeing, but outcomes cannot be guaranteed. If your needs exceed the scope of our services, appropriate referrals will be provided.
              </p>
            </div>

            <div id="changes" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">16. Changes to Terms</h2>
              <p className="body-medium">
                These terms may be updated from time to time. Significant changes will be communicated to active clients. Continued use of our services constitutes acceptance of any updated terms.
              </p>
            </div>

            <div id="contact" className="legal-section">
              <span className="section-label">Terms</span>
              <h2 className="heading-2">Contact Information</h2>
              <div className="legal-contact-box">
                <p className="body-medium"><strong>Clinical Minds Psychology</strong></p>
                <p className="body-medium">Lachlan Beaton, Clinical Psychologist (Registrar)</p>
                <p className="body-medium">AHPRA Registration: PSY0002785698</p>
                <p className="body-medium">Phone: <a href="tel:0266002710">02 6600 2710</a> | SMS: <a href="sms:0415220944">0415 220 944</a></p>
                <p className="body-medium">Email: <a href="mailto:contact@clinicalminds.net">contact@clinicalminds.net</a></p>
                <p className="body-medium">Address: 76 Albany Street, Coffs Harbour NSW 2450</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;
